import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Admin.css';

const formatPhp = (amount) => new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  maximumFractionDigits: 0
}).format(Number(amount || 0));

const Bookings = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const [calendarDate, setCalendarDate] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [slots, setSlots] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [paymentsMonth, setPaymentsMonth] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${yyyy}-${mm}`;
  });
  const [invoices, setInvoices] = useState([]);
  const [pendingCollections, setPendingCollections] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    fetchPayments(paymentsMonth);
  }, [paymentsMonth]);

  useEffect(() => {
    fetchSlots(calendarDate);
  }, [calendarDate]);

  const fetchBookings = async () => {
    try {
      const response = await api.get('/admin/bookings');
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('予約取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async (date) => {
    if (!date) return;
    try {
      setCalendarLoading(true);
      const res = await api.get('/admin/availability-slots', { params: { date } });
      setSlots(res.data.slots || []);
    } catch (error) {
      console.error('枠取得エラー:', error);
      setSlots([]);
    } finally {
      setCalendarLoading(false);
    }
  };

  const fetchPayments = async (month) => {
    try {
      setPaymentsLoading(true);
      const res = await api.get('/admin/payments', { params: { month } });
      setInvoices(res.data.invoices || []);
      setPendingCollections(res.data.pendingCollections || []);
    } catch (error) {
      console.error('支払い取得エラー:', error);
      setInvoices([]);
      setPendingCollections([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleSendReminder = async (studentId, month) => {
    try {
      await api.post('/admin/payments/remind', { studentId, month });
      alert('リマインドを送信しました');
    } catch (error) {
      alert(error?.response?.data?.error || 'リマインドを送信できませんでした');
    }
  };

  const handleRecordManualCollection = async (studentId, month) => {
    if (!window.confirm('手動入金として記録しますか？')) {
      return;
    }

    try {
      await api.post('/admin/payments/record-manual-collection', { studentId, month });
      alert('入金を記録しました');
      await fetchPayments(paymentsMonth);
    } catch (error) {
      alert(error?.response?.data?.error || '入金を記録できませんでした');
    }
  };

  const handleViewPaymentDetails = async (invoiceId) => {
    try {
      const res = await api.get(`/admin/payments/${invoiceId}`);
      const invoice = res.data.invoice;
      const items = res.data.items || [];
      const lines = items.map((it) => `- ${it.description || it.booking_id}: ${formatPhp(it.amount)}`).join('\n');
      alert(
        `支払い詳細\n\n月: ${invoice.billing_month}\n生徒: ${invoice.student_name} (${invoice.student_email})\n金額: ${formatPhp(invoice.amount)}\nステータス: ${invoice.status}\n\n内訳:\n${lines || '(内訳なし)'}`
      );
    } catch (error) {
      alert('詳細を取得できませんでした');
    }
  };

  const toDateKey = (dateObj) => {
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const isOverlapping = (startA, endA, startB, endB) => startA < endB && startB < endA;

  const myBookingsForDate = (dateKey) => {
    if (!user?.id) return [];
    return bookings.filter((b) => {
      if (b.teacher_id !== user.id) return false;
      const d = new Date(b.scheduled_at);
      return toDateKey(d) === dateKey && b.status !== 'cancelled';
    });
  };

  const handleCalendarSlotClick = async (hour) => {
    try {
      const start = new Date(calendarDate);
      start.setHours(hour, 0, 0, 0);
      await api.post('/admin/availability-slots', {
        startTime: start.toISOString(),
        durationMinutes: 60
      });
      await fetchSlots(calendarDate);
      await fetchBookings();
    } catch (error) {
      const message = error?.response?.data?.error || 'Failed to add slot';
      alert(message);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('この予約を削除してもよろしいですか？')) {
      return;
    }
    try {
      await api.delete(`/admin/bookings/${bookingId}`);
      fetchBookings();
      alert('予約を削除しました');
    } catch (error) {
      alert('削除できませんでした');
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return '保留';
      case 'confirmed': return '確定';
      case 'completed': return '完了';
      case 'cancelled': return 'キャンセル';
      default: return status;
    }
  };

  if (loading) {
    return <div className="loading">読み込み中...</div>;
  }

  return (
    <div className="admin-bookings">
      <div className="page-header">
        <h1>予約管理</h1>
        <p className="subtitle">全てのレッスン予約を管理できます</p>
      </div>

      <div className="admin-calendar-card">
        <div className="admin-calendar-header">
          <h2 className="admin-calendar-title">自分のレッスン枠（クリックで追加）</h2>
          <div className="admin-calendar-controls">
            <input
              type="date"
              className="admin-calendar-date"
              value={calendarDate}
              onChange={(e) => setCalendarDate(e.target.value)}
            />
          </div>
        </div>

        {calendarLoading ? (
          <div className="admin-calendar-loading">読み込み中...</div>
        ) : (
          <div className="admin-day-grid">
            {Array.from({ length: 12 }).map((_, idx) => {
              const hour = 9 + idx;
              const slotStart = new Date(calendarDate);
              slotStart.setHours(hour, 0, 0, 0);
              const slotEnd = new Date(slotStart.getTime() + 60 * 60000);

              const dayBookings = myBookingsForDate(calendarDate);
              const booking = dayBookings.find((b) => {
                const bStart = new Date(b.scheduled_at);
                const bEnd = new Date(bStart.getTime() + (b.duration_minutes || 60) * 60000);
                return isOverlapping(slotStart, slotEnd, bStart, bEnd);
              });

              const openSlot = slots.find((s) => {
                if (s.status !== 'open') return false;
                const sStart = new Date(s.start_at);
                return toDateKey(sStart) === calendarDate && sStart.getHours() === hour;
              });

              const isBlocked = !!booking || !!openSlot;

              return (
                <div key={hour} className="admin-day-row">
                  <div className="admin-day-time">{String(hour).padStart(2, '0')}:00</div>
                  <button
                    type="button"
                    className={`admin-day-cell ${isBlocked ? 'blocked' : 'clickable'}`}
                    onClick={() => !isBlocked && handleCalendarSlotClick(hour)}
                    disabled={isBlocked}
                    title={isBlocked ? '既に予定があります' : 'クリックして枠を追加'}
                  >
                    {booking ? (
                      <span className="admin-day-badge booked">予約: {booking.student_name}</span>
                    ) : openSlot ? (
                      <span className="admin-day-badge open">枠あり</span>
                    ) : (
                      <span className="admin-day-hint">＋ 追加</span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="filter-tabs">
        <button 
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          全て ({bookings.length})
        </button>
        <button 
          className={`filter-tab ${filter === 'confirmed' ? 'active' : ''}`}
          onClick={() => setFilter('confirmed')}
        >
          確定 ({bookings.filter(b => b.status === 'confirmed').length})
        </button>
        <button 
          className={`filter-tab ${filter === 'completed' ? 'active' : ''}`}
          onClick={() => setFilter('completed')}
        >
          完了 ({bookings.filter(b => b.status === 'completed').length})
        </button>
        <button 
          className={`filter-tab ${filter === 'cancelled' ? 'active' : ''}`}
          onClick={() => setFilter('cancelled')}
        >
          キャンセル ({bookings.filter(b => b.status === 'cancelled').length})
        </button>
      </div>

      <div className="bookings-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>日時</th>
              <th>生徒</th>
              <th>教師</th>
              <th>レッスン</th>
              <th>時間</th>
              <th>ステータス</th>
              <th>Meet</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.length > 0 ? (
              filteredBookings.map(booking => (
                <tr key={booking.id}>
                  <td>
                    <div className="date-cell">
                      <span className="date">{new Date(booking.scheduled_at).toLocaleDateString('ja-JP')}</span>
                      <span className="time">{new Date(booking.scheduled_at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </td>
                  <td>
                    <div className="user-info-cell">
                      <span className="name">{booking.student_name}</span>
                      <span className="email">{booking.student_email}</span>
                    </div>
                  </td>
                  <td>
                    <div className="user-info-cell">
                      <span className="name">{booking.teacher_name}</span>
                      <span className="email">{booking.teacher_email}</span>
                    </div>
                  </td>
                  <td>{booking.lesson_title || '-'}</td>
                  <td>{booking.duration_minutes}分</td>
                  <td>
                    <span className={`status-badge status-${booking.status}`}>
                      {getStatusLabel(booking.status)}
                    </span>
                  </td>
                  <td>
                    {booking.google_meet_link ? (
                      <a 
                        href={booking.google_meet_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="meet-link"
                      >
                        🔗 参加
                      </a>
                    ) : (
                      <span className="no-meet">-</span>
                    )}
                  </td>
                  <td>
                    <button 
                      className="btn-icon btn-danger" 
                      onClick={() => handleDeleteBooking(booking.id)}
                      title="削除"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  予約がありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bookings-table-container" style={{ marginTop: 24 }}>
        <div className="page-header" style={{ marginBottom: 12 }}>
          <h2>支払い管理</h2>
          <p className="subtitle">月ごとの支払い結果を確認できます</p>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <input
            type="month"
            className="admin-calendar-date"
            value={paymentsMonth}
            onChange={(e) => setPaymentsMonth(e.target.value)}
          />
          <button type="button" className="btn" onClick={() => fetchPayments(paymentsMonth)} disabled={paymentsLoading}>
            {paymentsLoading ? '読み込み中...' : '更新'}
          </button>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>月</th>
              <th>生徒</th>
              <th>金額</th>
              <th>ステータス</th>
              <th>日時</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length > 0 ? (
              invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.billing_month}</td>
                  <td>
                    <div className="user-info-cell">
                      <span className="name">{inv.student_name}</span>
                      <span className="email">{inv.student_email}</span>
                    </div>
                  </td>
                  <td>{formatPhp(inv.amount)}</td>
                  <td>
                    <span className={`status-badge status-${inv.status === 'paid' ? 'completed' : inv.status === 'failed' ? 'cancelled' : 'pending'}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td>{inv.created_at ? new Date(inv.created_at).toLocaleString('ja-JP') : '-'}</td>
                  <td>
                    <button type="button" className="btn" onClick={() => handleViewPaymentDetails(inv.id)}>
                      詳細
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 20 }}>
                  {paymentsLoading ? '読み込み中...' : '支払いデータがありません'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="page-header" style={{ marginTop: 24, marginBottom: 12 }}>
          <h2>集金対象</h2>
          <p className="subtitle">未回収の請求へリマインド送信や手動入金記録ができます</p>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>月</th>
              <th>生徒</th>
              <th>金額</th>
              <th>件数</th>
              <th>最終追加</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {pendingCollections.length > 0 ? (
              pendingCollections.map((item) => (
                <tr key={`${item.student_id}-${item.billing_month}`}>
                  <td>{item.billing_month}</td>
                  <td>
                    <div className="user-info-cell">
                      <span className="name">{item.student_name}</span>
                      <span className="email">{item.student_email}</span>
                    </div>
                  </td>
                  <td>{formatPhp(item.amount)}</td>
                  <td>{item.item_count}</td>
                  <td>{item.last_item_at ? new Date(item.last_item_at).toLocaleString('ja-JP') : '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" className="btn" onClick={() => handleSendReminder(item.student_id, item.billing_month)}>
                        督促通知
                      </button>
                      <button type="button" className="btn btn-primary" onClick={() => handleRecordManualCollection(item.student_id, item.billing_month)}>
                        入金記録
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 20 }}>
                  {paymentsLoading ? '読み込み中...' : '未回収の請求はありません'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Bookings;
